import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageContainer, PageContent } from '@/components/ui/page-layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui/table';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { getAdminStats, getAdminLogs } from '../services/apiService';
import type { AdminStats, AdminLogsResponse } from '../types/api';

const PAGE_SIZE = 50;

function fmtTime(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

export default function AdminPage() {
  const { t } = useTranslation('admin');

  // ── Statistics ──────────────────────────────────────────────────────────
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Top-pages view: 'app' hides scanner noise (e.g. /graphql); 'all' shows everything.
  const [pathScope, setPathScope] = useState<'app' | 'all'>('app');

  useEffect(() => {
    getAdminStats(30)
      .then(setStats)
      .catch(() => setError(t('error')))
      .finally(() => setStatsLoading(false));
  }, [t]);

  // ── Logs (paginated + filtered) ─────────────────────────────────────────
  const [logs, setLogs] = useState<AdminLogsResponse | null>(null);
  const [logsLoading, setLogsLoading] = useState(true);
  const [category, setCategory] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);

  // Debounce the search box into the actual query.
  useEffect(() => {
    const id = setTimeout(() => { setQ(search); setOffset(0); }, 350);
    return () => clearTimeout(id);
  }, [search]);

  const loadLogs = useCallback(() => {
    setLogsLoading(true);
    getAdminLogs({
      limit: PAGE_SIZE,
      offset,
      category: category === 'all' ? undefined : category,
      q: q || undefined,
    })
      .then(setLogs)
      .catch(() => setError(t('error')))
      .finally(() => setLogsLoading(false));
  }, [offset, category, q, t]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const total = logs?.total ?? 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <PageContainer>
      <PageContent>
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-ctu-text">{t('pageTitle')}</h1>
          <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="statistics">
          <TabsList className="mb-4">
            <TabsTrigger value="statistics">{t('tabs.statistics')}</TabsTrigger>
            <TabsTrigger value="logs">{t('tabs.logs')}</TabsTrigger>
          </TabsList>

          {/* ── Statistics ─────────────────────────────────────────────── */}
          <TabsContent value="statistics">
            <div className="grid gap-4 sm:grid-cols-3 mb-6">
              {([
                ['activeUsers1d', stats?.active_users_1d],
                ['activeUsers7d', stats?.active_users_7d],
                ['activeUsers30d', stats?.active_users_30d],
              ] as const).map(([key, value]) => (
                <Card key={key}>
                  <CardHeader className="pb-2">
                    <CardDescription>{t(`stats.${key}`)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {statsLoading
                      ? <Skeleton className="h-8 w-16" />
                      : <div className="text-3xl font-semibold">{value ?? 0}</div>}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
                  <CardTitle className="text-base">{t('stats.topPages')}</CardTitle>
                  {/* Switch: application-only (hide scanner noise) vs all paths */}
                  <div className="inline-flex rounded-md border p-0.5">
                    <Button
                      size="sm"
                      variant={pathScope === 'app' ? 'default' : 'ghost'}
                      className="h-7 px-2 text-xs"
                      onClick={() => setPathScope('app')}
                    >
                      {t('stats.scopeApp')}
                    </Button>
                    <Button
                      size="sm"
                      variant={pathScope === 'all' ? 'default' : 'ghost'}
                      className="h-7 px-2 text-xs"
                      onClick={() => setPathScope('all')}
                    >
                      {t('stats.scopeAll')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('stats.path')}</TableHead>
                        <TableHead className="text-right">{t('stats.count')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {((pathScope === 'app' ? stats?.top_paths_app : stats?.top_paths) ?? []).map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-xs">{r.path ?? '—'}</TableCell>
                          <TableCell className="text-right">{r.count}</TableCell>
                        </TableRow>
                      ))}
                      {!statsLoading && ((pathScope === 'app' ? stats?.top_paths_app : stats?.top_paths)?.length ?? 0) === 0 && (
                        <TableRow><TableCell colSpan={2} className="text-muted-foreground">{t('stats.noData')}</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">{t('stats.topUsers')}</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('stats.user')}</TableHead>
                        <TableHead className="text-right">{t('stats.count')}</TableHead>
                        <TableHead className="text-right">{t('stats.lastSeen')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(stats?.top_users ?? []).map((u, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs">{u.email ?? u.userId}</TableCell>
                          <TableCell className="text-right">{u.count}</TableCell>
                          <TableCell className="text-right text-xs">{u.lastSeen ? new Date(u.lastSeen).toLocaleDateString() : '—'}</TableCell>
                        </TableRow>
                      ))}
                      {!statsLoading && (stats?.top_users?.length ?? 0) === 0 && (
                        <TableRow><TableCell colSpan={3} className="text-muted-foreground">{t('stats.noData')}</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader><CardTitle className="text-base">{t('stats.loginsOverTime')}</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('logs.time')}</TableHead>
                        <TableHead className="text-right">{t('stats.count')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(stats?.logins_over_time ?? []).map((d, i) => (
                        <TableRow key={i}>
                          <TableCell>{d.day}</TableCell>
                          <TableCell className="text-right">{d.count}</TableCell>
                        </TableRow>
                      ))}
                      {!statsLoading && (stats?.logins_over_time?.length ?? 0) === 0 && (
                        <TableRow><TableCell colSpan={2} className="text-muted-foreground">{t('stats.noData')}</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ── Logs ───────────────────────────────────────────────────── */}
          <TabsContent value="logs">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Select value={category} onValueChange={(v) => { setCategory(v); setOffset(0); }}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder={t('logs.category')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('logs.all')}</SelectItem>
                  <SelectItem value="security">{t('logs.security')}</SelectItem>
                  <SelectItem value="usage">{t('logs.usage')}</SelectItem>
                </SelectContent>
              </Select>
              <Input
                className="max-w-xs"
                placeholder={t('logs.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('logs.time')}</TableHead>
                      <TableHead>{t('logs.category')}</TableHead>
                      <TableHead>{t('logs.event')}</TableHead>
                      <TableHead>{t('logs.user')}</TableHead>
                      <TableHead>{t('logs.method')}</TableHead>
                      <TableHead>{t('logs.pathCol')}</TableHead>
                      <TableHead>{t('logs.status')}</TableHead>
                      <TableHead>{t('logs.ip')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logsLoading && Array.from({ length: 8 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={8}><Skeleton className="h-5 w-full" /></TableCell>
                      </TableRow>
                    ))}
                    {!logsLoading && (logs?.items ?? []).map((row, i) => (
                      <TableRow key={i}>
                        <TableCell className="whitespace-nowrap text-xs">{fmtTime(row.occurredAt)}</TableCell>
                        <TableCell>
                          <Badge variant={row.category === 'security' ? 'destructive' : 'secondary'}>
                            {row.category}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{row.eventType}</TableCell>
                        <TableCell className="text-xs">{row.userEmail ?? row.userId ?? '—'}</TableCell>
                        <TableCell className="text-xs">{row.method ?? '—'}</TableCell>
                        <TableCell className="font-mono text-xs">{row.path ?? '—'}</TableCell>
                        <TableCell className="text-xs">{row.status ?? '—'}</TableCell>
                        <TableCell className="text-xs">{row.ip ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                    {!logsLoading && (logs?.items?.length ?? 0) === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-muted-foreground py-6 text-center">{t('logs.empty')}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-muted-foreground">
                {t('logs.showing', { from, to, total })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm"
                  disabled={offset === 0 || logsLoading}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  {t('logs.previous')}
                </Button>
                <Button
                  variant="outline" size="sm"
                  disabled={to >= total || logsLoading}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  {t('logs.next')}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </PageContent>
    </PageContainer>
  );
}
